import { workspaceFilesAvatarListHref } from '@/lib/workspace-avatar-files-route';
import { workspaceFilesMediaHref } from '@/lib/workspace-files-media';

/**
 * Single source of truth for global keyboard shortcuts. Bound by
 * `GlobalShortcuts` (Mousetrap sequences) and rendered as Kbd hints in the
 * ⌘K menu. `labelKey` lives in the CommandMenu i18n namespace.
 */
export type ShortcutDef = {
  id: string;
  /** Mousetrap binding string, e.g. 'g h' (sequence) or 'mod+j'. */
  sequence: string;
  /** Display tokens; 'mod' renders as ⌘ on Apple platforms, Ctrl elsewhere. */
  display: string[];
  labelKey: string;
  scope: 'global' | 'org';
  href?: (orgSlug: string) => string;
};

export const SHORTCUTS: ShortcutDef[] = [
  {
    id: 'nav.home',
    sequence: 'g h',
    display: ['G', 'H'],
    labelKey: 'home',
    scope: 'org',
    href: (orgSlug) => `/${orgSlug}`,
  },
  {
    id: 'nav.products',
    sequence: 'g p',
    display: ['G', 'P'],
    labelKey: 'products',
    scope: 'org',
    href: (orgSlug) => `/${orgSlug}/products`,
  },
  {
    id: 'nav.library',
    sequence: 'g l',
    display: ['G', 'L'],
    labelKey: 'library',
    scope: 'org',
    href: workspaceFilesMediaHref,
  },
  {
    id: 'nav.threads',
    sequence: 'g t',
    display: ['G', 'T'],
    labelKey: 'threads',
    scope: 'org',
    href: (orgSlug) => `/${orgSlug}/threads`,
  },
  {
    id: 'nav.settings',
    sequence: 'g s',
    display: ['G', 'S'],
    labelKey: 'settings',
    scope: 'org',
    href: (orgSlug) => `/${orgSlug}/settings`,
  },
  {
    id: 'nav.avatars',
    sequence: 'g a',
    display: ['G', 'A'],
    labelKey: 'avatars',
    scope: 'org',
    href: workspaceFilesAvatarListHref,
  },
  {
    id: 'nav.design',
    sequence: 'mod+j',
    display: ['mod', 'J'],
    labelKey: 'newThread',
    scope: 'org',
    href: (orgSlug) => `/${orgSlug}/design`,
  },
];

export function getShortcut(id: string): ShortcutDef | undefined {
  return SHORTCUTS.find((shortcut) => shortcut.id === id);
}

export function resolveDisplayToken(token: string, isApplePlatform: boolean) {
  if (token === 'mod') return isApplePlatform ? '⌘' : 'Ctrl';
  return token;
}

export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}
